import { useCallback, useEffect, useState } from "react";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";

/**
 * Fetches the active organization's quota snapshot (current + max per metric)
 * and exposes booleans for "at limit" so callers can disable mutation UI
 * (Create Project, Add Members, Task Import) before the backend has to
 * reject the attempt.
 *
 * Returns ``null`` while loading, and ``{ atMembers, atProjects, atTasks,
 * usage, refresh }`` once resolved. Unlimited quotas (max=null) never report
 * "at limit".
 */
export const useOrgQuota = () => {
  const api = useAPI();
  const { user } = useAuth();
  const activeOrg = user?.active_organization;
  const [usage, setUsage] = useState(null);

  const refresh = useCallback(async () => {
    if (!activeOrg) return;
    const res = await api.callApi("orgQuota", { params: { pk: activeOrg }, suppressError: true });
    if (res && !res.error) setUsage(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!usage) return null;

  const atLimit = (row) => row?.max != null && (row.current ?? 0) >= row.max;
  return {
    usage,
    atMembers: atLimit(usage.members),
    atProjects: atLimit(usage.projects),
    atTasks: atLimit(usage.tasks),
    // Per-project storage allowance + heaviest projects (see
    // Organization.storage_usage); null max = unlimited.
    storage: usage.storage ?? null,
    refresh,
  };
};
